import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input, InputPassword } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { extractErrorMessage } from "@/lib/utils";
import { MailboxType } from "@/types/mailbox.types";
import { PlayCircle } from "lucide-react";
import * as React from "react";
import { useState } from "react";
import { toast } from "sonner";
import { useServices } from "@/services/hooks/useServices";
import { useAddAzureSmtp } from "@/hooks/use-mailbox";

interface AzureSmtpWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AzureSmtpWizard({ open, onOpenChange }: AzureSmtpWizardProps) {
  const [step, setStep] = React.useState(1);
  const [formData, setFormData] = React.useState<Partial<MailboxType>>({
    firstName: "",
    lastName: "",
    email: "",
    smtpHost: "",
    smtpPort: 587,
    smtpPassword: "",
    smtpTls: true,
    imapHost: "",
    imapPort: 993,
    imapPassword: "",
    imapTls: true,
  });

  const { mutateAsync: addAzureSmtp, isPending: isCreating } = useAddAzureSmtp();
  const { mailboxService } = useServices();
  const [isValidating, setIsValidating] = useState(false);
  const isLoading = isCreating || isValidating;

  const updateFormData = (field: string, value: string | boolean | number) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = async () => {
    if (step === 1) {
      if (!formData.firstName || !formData.lastName || !formData.email) {
        toast.error("Please fill in all personal details");
        return;
      }
    }

    if (step === 2) {
      if (!formData.smtpHost || !formData.smtpPort || !formData.smtpPassword) {
        toast.error("Please fill in all SMTP details");
        return;
      }

      setIsValidating(true);
      try {
        const smtpResponse = await mailboxService.validateSmtp({
          auth: { user: formData.email!, pass: formData.smtpPassword! },
          host: formData.smtpHost!,
          port: formData.smtpPort!,
          secure: formData.smtpTls!,
        });

        if (smtpResponse.success) {
          toast.success("SMTP credentials verified successfully");
          setStep(step + 1);
        } else {
          toast.error("Invalid SMTP credentials. Please check and try again.");
        }
      } catch (error) {
        toast.error(
          "Failed to validate SMTP credentials. Please check your settings."
        );
      } finally {
        setIsValidating(false);
      }
      return;
    }

    if (step < 3) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) setStep(step - 1);
  };

  const resetForm = () => {
    setStep(1);
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      smtpHost: "",
      smtpPort: 587,
      smtpPassword: "",
      smtpTls: true,
      imapHost: "",
      imapPort: 993,
      imapPassword: "",
      imapTls: true,
    });
  };

  const handleSubmit = async () => {
    try {
      if (step === 3) {
        setIsValidating(true);

        // Validate IMAP credentials
        const imapResponse = await mailboxService.validateImap({
          auth: { user: formData.email!, pass: formData.imapPassword! },
          host: formData.imapHost!,
          port: formData.imapPort!,
          secure: formData.imapTls!,
        });

        if (!imapResponse.success) {
          toast.error("Invalid IMAP credentials. Please check and try again.");
          return;
        }

        toast.success("IMAP credentials verified successfully");

        // Submit to Azure endpoint
        await addAzureSmtp({
          firstName: formData.firstName || "",
          lastName: formData.lastName || "",
          email: formData.email!,
          smtpHost: formData.smtpHost!,
          smtpPort: formData.smtpPort || 587,
          smtpUsername: formData.email!,
          smtpPassword: formData.smtpPassword!,
          smtpTls: formData.smtpTls ? "TLS" : "NONE",
          imapHost: formData.imapHost!,
          imapPort: formData.imapPort || 993,
          imapPassword: formData.imapPassword!,
          imapTls: formData.imapTls || false,
        });

        toast.success("Azure mailbox created successfully");
        window.cioanalytics?.track("mailbox_added", {
          email: formData.email,
          provider: "azure",
        });

        onOpenChange(false);
        resetForm();
      } else {
        handleNext();
      }
    } catch (error) {
      console.error("Validation/Creation error:", error);
      const message = extractErrorMessage(
        error,
        "Failed to create Azure mailbox. Please try again."
      );
      toast.error(message);
    } finally {
      setIsValidating(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) resetForm();
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            Add Azure Mailbox
          </DialogTitle>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex justify-center">
          <div className="flex items-center">
            {[1, 2, 3].map((i) => (
              <React.Fragment key={i}>
                <div
                  className={`flex h-8 w-8 items-center justify-center rounded-full ${
                    step >= i ? "bg-blue-600 text-white" : "bg-muted"
                  }`}
                >
                  {i}
                </div>
                {i < 3 && (
                  <div
                    className={`h-1 w-10 ${
                      step > i ? "bg-blue-600" : "bg-muted"
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="pt-4">
          {/* Step 1: Personal Information */}
          {step === 1 && (
            <div className="space-y-4">
              <div className="flex justify-center">
                <Button
                  variant="outline"
                  className="flex items-center gap-2 h-10 px-4 w-full sm:max-w-sm"
                  onClick={() =>
                    window.open("https://youtu.be/foSSqoX4rtI", "_blank")
                  }
                >
                  <PlayCircle className="h-5 w-5" />
                  <span className="font-medium">See tutorial video</span>
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="firstName">First Name</Label>
                  <Input
                    id="firstName"
                    placeholder="First Name"
                    value={formData.firstName}
                    onChange={(e) => updateFormData("firstName", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="lastName">Last Name</Label>
                  <Input
                    id="lastName"
                    placeholder="Last Name"
                    value={formData.lastName}
                    onChange={(e) => updateFormData("lastName", e.target.value)}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@example.com"
                  value={formData.email}
                  onChange={(e) => updateFormData("email", e.target.value)}
                />
              </div>
            </div>
          )}

          {/* Step 2: SMTP Configuration */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="smtpHost">SMTP Host</Label>
                <Input
                  id="smtpHost"
                  placeholder="smtp.azurecomm.net"
                  value={formData.smtpHost}
                  onChange={(e) => updateFormData("smtpHost", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtpPort">SMTP Port</Label>
                <Input
                  id="smtpPort"
                  type="number"
                  placeholder="587"
                  value={formData.smtpPort}
                  onChange={(e) =>
                    updateFormData("smtpPort", parseInt(e.target.value))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="smtpPassword">SMTP Password</Label>
                <InputPassword
                  id="smtpPassword"
                  placeholder="Enter SMTP Password"
                  value={formData.smtpPassword}
                  onChange={(e) =>
                    updateFormData("smtpPassword", e.target.value)
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="smtpTls">Enable TLS for SMTP</Label>
                <Switch
                  id="smtpTls"
                  checked={formData.smtpTls}
                  onCheckedChange={(checked) => updateFormData("smtpTls", checked)}
                />
              </div>
            </div>
          )}

          {/* Step 3: IMAP Configuration */}
          {step === 3 && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="imapHost">IMAP Host</Label>
                <Input
                  id="imapHost"
                  placeholder="outlook.office365.com"
                  value={formData.imapHost}
                  onChange={(e) => updateFormData("imapHost", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="imapPort">IMAP Port</Label>
                <Input
                  id="imapPort"
                  type="number"
                  placeholder="993"
                  value={formData.imapPort}
                  onChange={(e) =>
                    updateFormData("imapPort", parseInt(e.target.value))
                  }
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="imapPassword">IMAP Password</Label>
                <InputPassword
                  id="imapPassword"
                  placeholder="Enter IMAP Password"
                  value={formData.imapPassword}
                  onChange={(e) =>
                    updateFormData("imapPassword", e.target.value)
                  }
                />
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="imapTls">Enable TLS for IMAP</Label>
                <Switch
                  id="imapTls"
                  checked={formData.imapTls}
                  onCheckedChange={(checked) => updateFormData("imapTls", checked)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="flex justify-between pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleBack}
            disabled={step === 1 || isLoading}
          >
            Back
          </Button>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={() => onOpenChange(false)}
              disabled={isLoading}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isLoading}>
              {isLoading
                ? "Processing..."
                : step === 3
                ? "Submit"
                : "Next"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
